// Shared WhatsApp share-message building/chunking logic — used by the
// public map's WhatsAppHandler.jsx and the admin panel's Lands tab, so the
// message format/limits stay identical everywhere a land gets shared from
// the web app. (mobile/src/utils/whatsapp.js mirrors this for the Expo
// app, which can't import this file directly.)

// WhatsApp's own hard limit on message body length.
export const MAX_WHATSAPP_LENGTH = 65536;
// Cap on how many properties go into a single shared message, so long
// selections get split into multiple groups instead of one unreadable
// wall of text.
export const MAX_ITEMS_PER_MESSAGE = 35;

const APP_STORE_URL = 'https://apps.apple.com/kw/app/ba7ria-بحرية/id6792278900';

const t = (language, arabicText, englishText) => (language === 'ar' ? arabicText : englishText);

// Chalet coordinates/land_number/kuwait_finder_link/additional_pictures are
// already redacted server-side for non-staff (see backend's
// landsController.js redactChaletFieldsIfNeeded) — `is_chalet` itself is
// stripped from every response though, so this re-derives it from the
// fields that do survive.
export const isChaletLand = (land) =>
  land.land_type_info?.name === 'chalet' || (land.tags || []).some((tag) => tag.title === 'شاليهات');

export const generateGoogleMapsLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

export const generateGalleryLink = (land) => `${window.location.origin}/gallery/${land.id}`;

// Every WhatsApp share (single or bulk, from the public map or the admin
// Lands tab) ends with this — the existing branded signature plus a
// download-the-app call to action.
export const footerText = (language) =>
  `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
  `" ${t(language, 'دروازة الصفاة العقارية', 'Darwaza Al-Safa Real Estate')} "\n\n` +
  `*${t(language, 'فكر عقاري مختلف', 'A Different Real Estate Mindset')}*\n` +
  `*${t(language, 'معكم من ٢٠٠٧', 'With you since 2007')}*\n` +
  `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
  `${t(language, 'لعروض أكثر حمل تطبيق ba7ria', 'For more offers, download the ba7ria app')}\n` +
  `${APP_STORE_URL}`;

export const buildLandBlock = (land, { language, isStaff = false }) => {
  let message = '';

  if (land.code_number) message += `${t(language, 'كود', 'Code')} : ${land.code_number}\n\n`;

  // A non-staff share of a chalet gets only the code + details — every
  // other field either leaks a chalet's normally-redacted location (maps
  // link, plot number) or is meaningless without it (gallery/finder
  // link), and would otherwise render broken (e.g. a maps link built from
  // null coordinates) since those fields are already nulled server-side.
  // Always false for admin-triggered shares — staff already see everything.
  if (isChaletLand(land) && !isStaff) {
    if (land.notes) message += `${t(language, 'التفاصيل', 'Details')} : ${land.notes}\n\n`;
    return message;
  }

  if (land.land_number) message += `${t(language, 'قسيمة', 'Plot')} : ${land.land_number}\n\n`;
  if (land.notes) message += `${t(language, 'التفاصيل', 'Details')} : ${land.notes}\n\n`;

  if (land.price) {
    const priceFormatted = new Intl.NumberFormat('en-US').format(land.price);
    message += `${t(language, 'السعر', 'Price')}: ${priceFormatted} ${t(language, 'دك', 'KD')}\n\n`;
  }

  message += `${t(language, 'رابط جوجل ماب', 'Google Maps Link')} : \n${generateGoogleMapsLink(land.latitude, land.longitude)}\n\n`;
  message += `${t(language, 'المخططات والصور', 'Plans and Pictures')} : \n${generateGalleryLink(land)}\n\n`;

  if (land.kuwait_finder_link) {
    message += `${t(language, 'رابط كويت فايندر', 'Kuwait Finder Link')} : \n${land.kuwait_finder_link}\n\n`;
  }

  return message;
};

export const generateWhatsAppMessage = (land, options) => `${buildLandBlock(land, options)}${footerText(options.language)}`;

// Splits a bulk selection into as many messages as needed to keep each
// group at or under MAX_ITEMS_PER_MESSAGE properties (and, as a safety
// net, under WhatsApp's 65,536-character limit too) — grouping whole
// lands together (never splitting a single land's block across messages).
// Returns [{ message, count }, ...].
export const splitBulkWhatsAppMessages = (selectedLands, options) => {
  const separator = `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
  const footer = `\n${footerText(options.language)}`;
  const chunks = [];
  let current = '';
  let count = 0;

  selectedLands.forEach((land) => {
    const block = buildLandBlock(land, options);
    const piece = count === 0 ? block : separator + block;

    const wouldExceedItems = count >= MAX_ITEMS_PER_MESSAGE;
    const wouldExceedLength = current && current.length + piece.length + footer.length > MAX_WHATSAPP_LENGTH;

    if (current && (wouldExceedItems || wouldExceedLength)) {
      chunks.push({ message: current + footer, count });
      current = block;
      count = 1;
    } else {
      current += piece;
      count += 1;
    }
  });

  if (current || chunks.length === 0) {
    chunks.push({ message: current + footer, count });
  }

  return chunks;
};

// Opens a wa.me chat with `message` prefilled — WhatsApp's own contact
// picker if `phone` is omitted, a specific number's chat otherwise. This
// only ever opens a pre-filled draft; a human still has to hit Send inside
// WhatsApp — there is no backend WhatsApp API in this app to send without
// that.
export const openWhatsAppShare = (message, phone) => {
  const url = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank');
};
